import React, { useState } from "react";
import { ChevronDown, Globe, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";

// Destinos canónicos (Ventas, oct-2026): evita «Italy / Italia» o «Spain / España» mezclados.
// Trips trabaja en español; el Cotizador (documentos para el cliente) en inglés.
export const DESTINATIONS = ["España", "Italia", "Portugal", "Malta", "Marruecos"];
export const DESTINATIONS_EN = ["Spain", "Italy", "Portugal", "Malta", "Morocco"];
const ES_TO_EN = { "España": "Spain", "Italia": "Italy", "Portugal": "Portugal", "Malta": "Malta", "Marruecos": "Morocco" };
const EN_TO_ES = Object.fromEntries(Object.entries(ES_TO_EN).map(([a, b]) => [b, a]));
const ALIASES = {
  "españa": "España", "espana": "España", "spain": "España", "spagna": "España", "espagne": "España",
  "italia": "Italia", "italy": "Italia", "italie": "Italia",
  "portugal": "Portugal",
  "malta": "Malta",
  "marruecos": "Marruecos", "morocco": "Marruecos", "maroc": "Marruecos", "marocco": "Marruecos",
};
export const SEP = " · ";

const canonFor = (es, lang) => (lang === "en" ? ES_TO_EN[es] || es : es);
const listFor = (lang) => (lang === "en" ? DESTINATIONS_EN : DESTINATIONS);

// «Spain · Italy · España» → ["España", "Italia"] (o ["Spain", "Italy"] con lang "en");
// lo no reconocido se conserva tal cual.
export const parseDestinations = (text, lang = "es") => {
  const out = [];
  (text || "").split(/\s*(?:·|,|\/|\+|&)\s*/).map((p) => p.trim()).filter(Boolean).forEach((p) => {
    const es = ALIASES[p.toLowerCase()] || EN_TO_ES[p] || p;
    const canon = canonFor(es, lang);
    if (!out.includes(canon)) out.push(canon);
  });
  return out;
};
const sortCanon = (list, lang) => {
  const order = listFor(lang);
  return [...list].sort((a, b) => {
    const ia = order.indexOf(a), ib = order.indexOf(b);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib) || a.localeCompare(b);
  });
};
export const displayDestination = (text, lang = "es") => parseDestinations(text, lang).join(SEP);

export function DestinationMultiSelect({ value, onChange, testid = "destination-select", lang = "es", className = "" }) {
  const [open, setOpen] = useState(false);
  const options = listFor(lang);
  const selected = parseDestinations(value, lang);
  const toggle = (d) => {
    const next = selected.includes(d) ? selected.filter((x) => x !== d) : [...selected, d];
    onChange(sortCanon(next, lang).join(SEP));
  };
  const others = selected.filter((d) => !options.includes(d));
  const t = lang === "en"
    ? { empty: "Choose destination(s)…", multi: "You can pick several (combined trips)", legacy: "Non-standard legacy value", legacyHint: "Remove it with × and pick from the list." }
    : { empty: "Elegir destino(s)…", multi: "Puedes marcar varios (viajes combinados)", legacy: "Valor antiguo no estándar", legacyHint: "Quítalo con la × y elige el destino de la lista." };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={`flex min-h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-1.5 text-left text-sm shadow-sm hover:bg-accent/40 focus:outline-none focus:ring-1 focus:ring-ring ${className}`} data-testid={testid}>
          <span className="flex min-w-0 flex-wrap items-center gap-1">
            {selected.length === 0 && <span className="flex items-center gap-1.5 text-muted-foreground"><Globe className="h-3.5 w-3.5" /> {t.empty}</span>}
            {selected.map((d) => (
              <span key={d} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${options.includes(d) ? "bg-primary/10 text-primary" : "bg-amber-100 text-amber-800"}`} data-testid={`${testid}-chip-${d}`}>
                {d}
                <span role="button" tabIndex={-1} onClick={(e) => { e.stopPropagation(); toggle(d); }} className="rounded-full hover:bg-black/10" aria-label={`Quitar ${d}`} data-testid={`${testid}-remove-${d}`}><X className="h-3 w-3" /></span>
              </span>
            ))}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2" data-testid={`${testid}-options`}>
        <p className="mb-1.5 px-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t.multi}</p>
        {options.map((d) => (
          <label key={d} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent" data-testid={`${testid}-opt-${d}`}>
            <Checkbox checked={selected.includes(d)} onCheckedChange={() => toggle(d)} />
            {d}
          </label>
        ))}
        {others.length > 0 && (
          <p className="mt-2 border-t px-1 pt-2 text-[10px] text-amber-700">{t.legacy}: {others.join(", ")}. {t.legacyHint}</p>
        )}
      </PopoverContent>
    </Popover>
  );
}
